import {
  Box,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from '@mui/material';
import { ReactElement, useEffect, useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import EmptyPlaceholder from 'shared/components/EmptyPlaceholder';
import { ConfirmDialog } from 'shared/components/AppComponents';
import { ManageTeamService } from 'services/ManageTeamService';
import { Toast } from 'shared/helpers';
import { ITeamMember } from './types';
import { useDispatch, useSelector } from 'react-redux';
import { IAppState } from 'state/RootReducer';
import { PaginationActions } from 'state/pagination/Action';
import { formatRoleName } from './PendingInvitesTab';

const TeamMembersTab = (): ReactElement => {
  const dispatch = useDispatch();
  const teamsPagination = useSelector(
    (state: IAppState) => state.paginationState.teams
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [teamMembers, setTeamMembers] = useState<ITeamMember[]>([]);
  const [openDelete, setOpenDelete] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<ITeamMember | null>(
    null
  );
  const { authState } = useSelector((state: IAppState) => state);
  const [isDeleting, setIsDeleting] = useState(false);

  // API Calls
  const loadTeamMembers = (): void => {
    setIsLoading(true);
    const queryString = new URLSearchParams({
      offset: String(teamsPagination.offset),
      limit: String(teamsPagination.pageLimit),
      search: teamsPagination.search,
    }).toString();
    ManageTeamService.teamMembers(queryString)
      .then((response) => {
        const { user_list, pagination } = response.data.data;

        if (user_list.length > 0) {
          setTeamMembers(user_list);

          dispatch(
            PaginationActions.setPagination({
              teams: {
                offset: pagination.offset,
                pageLimit: pagination.limit,
                totalRecords: pagination.total,
                totalPages: pagination.totalPages,
              },
            })
          );
        } else {
          setTeamMembers([]);
        }
      })
      .catch((error: any) => {
        Toast.showInfoMessage(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  // UI Actions
  const handleCloseDelete = (): void => {
    setOpenDelete(false);
  };

  // Instance Methods
  const handlePageChange = (_: unknown, page: number): void => {
    dispatch(
      PaginationActions.setPagination({
        teams: {
          offset: page + 1,
        },
      })
    );
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const pageLimit = Number(event.target.value);

    dispatch(
      PaginationActions.setPagination({
        teams: {
          offset: 1,
          pageLimit,
        },
      })
    );
  };

  const handleDelete = (row: ITeamMember): void => {
    setIsDeleting(true);
    ManageTeamService.deleteTeamMember(row._id)
      .then(() => {
        Toast.showSuccessMessage('Successfully deleted team member');
        setOpenDelete(false);
        loadTeamMembers();
      })
      .catch((error) => {
        Toast.showInfoMessage(error.message);
      })
      .finally(() => {
        setIsDeleting(false);
      });
  };

  const actionOnTeamMembersDelete = (row: ITeamMember): void => {
    setOpenDelete(true);
    setEditingEmployee(row);
  };

  // Hooks
  useEffect(() => {
    loadTeamMembers();
  }, [
    teamsPagination.offset,
    teamsPagination.pageLimit,
    teamsPagination.search,
  ]);

  // UI Elements
  return (
    <>
      <Box className="app-plain-card">
        <Box className="content">
          {isLoading && (
            <Box className="empty-content">
              <CircularProgress />
            </Box>
          )}
          {!isLoading && teamMembers.length === 0 && (
            <Box className="empty-content">
              <EmptyPlaceholder title="No record found" />
            </Box>
          )}
          {!isLoading && teamMembers.length > 0 && (
            <TableContainer>
              <Table className="app-table">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {teamMembers.map((row) => (
                    <TableRow key={row._id}>
                      <TableCell className="word-break">{row.name}</TableCell>
                      <TableCell>{row.email}</TableCell>
                      <TableCell>
                        {formatRoleName(row.role) ||
                          formatRoleName(row.userRole)}
                      </TableCell>
                      <TableCell>
                        {authState.userId !== row.userId && (
                          <Box
                            style={{
                              display: 'flex',
                              columnGap: '10px',
                            }}
                          >
                            <IconButton
                              onClick={() => {
                                actionOnTeamMembersDelete(row);
                              }}
                              disabled={isDeleting}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {!isLoading && teamMembers.length > 0 && (
            <TablePagination
              component="div"
              count={teamsPagination.totalRecords}
              page={teamsPagination.offset - 1}
              rowsPerPage={teamsPagination.pageLimit}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[10, 20, 50]}
              disabled={isLoading}
            />
          )}
        </Box>
      </Box>

      <ConfirmDialog
        title="Delete Team Member"
        subTitle="Are you sure to delete the team member?"
        showConfirm={openDelete}
        cancelTitle="Close"
        successTitle="Delete"
        onSuccess={() => {
          if (editingEmployee) {
            handleDelete(editingEmployee);
          }
        }}
        onCancel={handleCloseDelete}
        onCloseDialog={handleCloseDelete}
      />
    </>
  );
};

export default TeamMembersTab;
